import React, { useMemo } from 'react'
import { X, Sun, Battery, Zap, Info, Activity, DollarSign } from 'lucide-react'
import { useEnergyStore } from '@/stores/useEnergyStore'
import { calculateMonthlyMetrics } from '@/engine/calculateMonthlyMetrics'
import { LOCATION_DATA } from '@/data/locationData'
import type { PlanRates, ConfigId } from '@/types'
import { useSimulationData } from '@/hooks/useSimulationData'

interface PlanBreakdownModalProps {
  isOpen: boolean
  onClose: () => void
  darkMode: boolean
  plan: PlanRates | null
}

export const PlanBreakdownModal: React.FC<PlanBreakdownModalProps> = ({
  isOpen,
  onClose,
  darkMode,
  plan
}) => {
  const config = useEnergyStore((s) => s.config)
  const selectedConfigId = useEnergyStore((s) => s.selectedConfig)
  const setSelectedConfig = useEnergyStore((s) => s.setSelectedConfig)
  const { data: simulationMonthlyData } = useSimulationData()

  const breakdown = useMemo(() => {
    if (!plan) return null

    const locData = LOCATION_DATA[config.location]
    const locMult = config.location === 'Custom' ? (config.customLocationMultiplier ?? 1.0) : (locData?.multiplier ?? 1.0)
    const locPriceMult = locData?.priceMult ?? 1.0

    const calcForConfig = (configId: ConfigId) => {
      let fixed = 0
      let imports = 0
      let selfUse = 0
      let exports = 0
      let net = 0
      let totalLoad = 0
      let totalGrossSelfUseKwh = 0
      let totalImportsKwh = 0
      let totalSolarGenKwh = 0
      let totalExportKwh = 0
      let evMonthly = 0
      let rawCost = 0

      for (let i = 0; i < 16; i++) {
        const simAdjust = simulationMonthlyData.length > 0 ? (simulationMonthlyData[i % 12] ?? 0) : 0
        
        // Compute for this config
        const metrics = calculateMonthlyMetrics(
          i,
          config,
          plan,
          configId,
          locMult,
          locPriceMult,
          simAdjust
        )

        // Compute for raw (Grid Only) config as reference
        const rawMetrics = calculateMonthlyMetrics(
          i,
          config,
          plan,
          'raw',
          locMult,
          locPriceMult,
          simAdjust
        )

        fixed += metrics.fixedChargeCost ?? 0
        imports += metrics.importCost ?? 0
        selfUse += metrics.selfUseSavings ?? 0
        exports += metrics.exportCredit ?? 0
        net += metrics.netCost
        totalLoad += metrics.totalLoadKwh
        totalGrossSelfUseKwh += metrics.selfConsKwh
        totalImportsKwh += metrics.gridConsumptionKwh
        totalSolarGenKwh += metrics.solarGenerationKwh ?? 0
        totalExportKwh += metrics.exportKwh ?? 0
        evMonthly += metrics.monthlyEvCost
        rawCost += rawMetrics.netCost
      }

      const totalNetSelfUseKwh = Math.max(0, totalLoad - totalImportsKwh)
      const netVariableCost = Math.max(0, imports - selfUse)
      const avgGridImportRate = totalImportsKwh > 0 ? netVariableCost / totalImportsKwh : 0
      const solarSavings = Math.max(0, rawCost - net)

      return {
        fixed: Math.round(fixed * (12 / 16)),
        imports: Math.round(imports * (12 / 16)),
        selfUse: Math.round(selfUse * (12 / 16)),
        exports: Math.round(exports * (12 / 16)),
        net: Math.round(net * (12 / 16)),
        totalLoad: Math.round(totalLoad * (12 / 16)),
        totalGrossSelfUseKwh: Math.round(totalGrossSelfUseKwh * (12 / 16)),
        totalNetSelfUseKwh: Math.round(totalNetSelfUseKwh * (12 / 16)),
        totalSolarGenKwh: Math.round(totalSolarGenKwh * (12 / 16)),
        totalExportKwh: Math.round(totalExportKwh * (12 / 16)),
        totalImportsKwh: Math.round(totalImportsKwh * (12 / 16)),
        avgGridImportRate,
        solarSavings: Math.round(solarSavings * (12 / 16)),
        evMonthly: Math.round(evMonthly / 16)
      }
    }

    return {
      raw: calcForConfig('raw'),
      solar: calcForConfig('solar'),
      solar_bat: calcForConfig('solar_bat')
    }
  }, [plan, config, simulationMonthlyData])

  if (!isOpen || !plan || !breakdown) return null

  const activeBreakdown = breakdown[selectedConfigId]
  const isSolar = config.pvSize > 0 && config.useSolar !== false && selectedConfigId !== 'raw'

  const {
    fixed,
    imports,
    selfUse,
    exports,
    net,
    totalLoad,
    totalGrossSelfUseKwh,
    totalNetSelfUseKwh,
    totalSolarGenKwh,
    totalExportKwh,
    totalImportsKwh,
    avgGridImportRate,
    solarSavings,
    evMonthly
  } = activeBreakdown

  const floorAdjustment = Math.max(0, net - (fixed + imports - selfUse - exports))

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 md:p-8">
      <div className="absolute inset-0 bg-slate-950/60 backdrop-blur-md" onClick={onClose} />
      <div
        className={`relative w-full max-w-4xl max-h-full overflow-hidden rounded-[2.5rem] border shadow-2xl animate-in zoom-in-95 duration-300 flex flex-col ${
          darkMode ? 'bg-slate-950 border-white/10' : 'bg-white border-slate-200'
        }`}
      >
        {/* Header */}
        <div className={`flex items-center justify-between p-6 border-b ${darkMode ? 'border-white/5' : 'border-slate-100'}`}>
          <div>
            <h2 className={`text-lg font-black uppercase tracking-tight flex items-center gap-2 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
              <Activity size={18} className="text-emerald-500" />
              {plan.plan} Breakdown
            </h2>
            <p className={`text-xs font-bold ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
              Detailed ledger & energy flow metrics (Annualised)
            </p>
          </div>
          <button
            onClick={onClose}
            className={`p-2.5 rounded-xl transition-all ${
              darkMode ? 'hover:bg-white/5 text-slate-500 hover:text-white' : 'hover:bg-slate-100 text-slate-400 hover:text-slate-900'
            }`}
          >
            <X size={18} />
          </button>
        </div>

        {/* Configuration Tabs */}
        <div className={`px-6 py-4 border-b flex flex-wrap gap-2 ${darkMode ? 'border-white/5 bg-white/5' : 'border-slate-100 bg-slate-50'}`}>
          {config.pvSize > 0 && config.useSolar !== false && (
            <button
              onClick={() => setSelectedConfig('solar')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all cursor-pointer border ${
                selectedConfigId === 'solar'
                  ? 'bg-amber-500 border-amber-600 text-white shadow-sm'
                  : darkMode
                    ? 'bg-slate-900 border-white/5 text-slate-400 hover:bg-slate-800'
                    : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-100'
              }`}
            >
              <Sun size={12} />
              Solar Only
            </button>
          )}

          {config.useEss && config.useSolar !== false && (
            <button
              onClick={() => setSelectedConfig('solar_bat')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all cursor-pointer border ${
                selectedConfigId === 'solar_bat'
                  ? 'bg-emerald-500 border-emerald-600 text-white shadow-sm'
                  : darkMode
                    ? 'bg-slate-900 border-white/5 text-slate-400 hover:bg-slate-800'
                    : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-100'
              }`}
            >
              <Battery size={12} />
              Solar + Battery
            </button>
          )}

          <button
            onClick={() => setSelectedConfig('raw')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all cursor-pointer border ${
              selectedConfigId === 'raw'
                ? 'bg-blue-500 border-blue-600 text-white shadow-sm'
                : darkMode
                  ? 'bg-slate-900 border-white/5 text-slate-400 hover:bg-slate-800'
                  : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-100'
            }`}
          >
            <Zap size={12} />
            Grid Only
          </button>
        </div>

        {/* Content */}
        <div className="p-6 md:p-8 overflow-y-auto max-h-[calc(100vh-220px)] custom-scrollbar">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
            
            {/* Financial Ledger Card */}
            <div className={`p-5 rounded-2xl border ${darkMode ? 'bg-slate-900/40 border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}>
              <h4 className={`text-[10px] font-black uppercase tracking-wider mb-4 flex items-center gap-1.5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                <DollarSign size={12} className="text-emerald-500" />
                Financial Ledger
              </h4>

              <div className="space-y-2.5">
                <div className="flex items-center justify-between text-xs">
                  <span className={`flex items-center gap-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    <button
                      onClick={() => useEnergyStore.getState().openHelpToItem('Fixed Charge Cost')}
                      className={`shrink-0 p-0.5 rounded-full transition-colors cursor-pointer ${
                        darkMode ? 'text-slate-600 hover:text-emerald-500' : 'text-slate-400 hover:text-emerald-600'
                      }`}
                      title="Help with Fixed Charge Cost"
                    >
                      <Info size={10} />
                    </button>
                    Fixed Charge Cost
                  </span>
                  <span className={`font-mono font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                    +${fixed.toLocaleString()}
                  </span>
                </div>

                <div className="flex items-center justify-between text-xs">
                  <span className={`flex items-center gap-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    <button
                      onClick={() => useEnergyStore.getState().openHelpToItem('Variable Grid Import Charges')}
                      className={`shrink-0 p-0.5 rounded-full transition-colors cursor-pointer ${
                        darkMode ? 'text-slate-600 hover:text-emerald-500' : 'text-slate-400 hover:text-emerald-600'
                      }`}
                      title="Help with Variable Grid Import Charges"
                    >
                      <Info size={10} />
                    </button>
                    Variable Grid Import Charges
                  </span>
                  <span className={`font-mono font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                    +${imports.toLocaleString()}
                  </span>
                </div>

                <div className={`flex items-center justify-between text-xs transition-colors duration-300 ${
                  isSolar 
                    ? 'text-emerald-500 font-bold' 
                    : `${darkMode ? 'text-slate-600' : 'text-slate-300'} opacity-40`
                }`}>
                  <span className="flex items-center gap-1">
                    <button
                      onClick={() => useEnergyStore.getState().openHelpToItem('Solar Self-Consumption Benefit')}
                      className={`shrink-0 p-0.5 rounded-full transition-colors cursor-pointer ${
                        isSolar
                          ? darkMode ? 'text-emerald-500/40 hover:text-emerald-300' : 'text-emerald-600/40 hover:text-emerald-700'
                          : darkMode ? 'text-slate-700 hover:text-slate-500' : 'text-slate-300 hover:text-slate-450'
                      }`}
                      title="Help with Solar Self-Consumption Benefit"
                    >
                      <Info size={10} />
                    </button>
                    <Sun size={10} /> Solar Self-Consumption Benefit
                  </span>
                  <span className="font-mono">
                    -${isSolar ? selfUse.toLocaleString() : '0'}
                  </span>
                </div>

                <div className={`flex items-center justify-between text-xs transition-colors duration-300 ${
                  isSolar 
                    ? 'text-emerald-500 font-bold' 
                    : `${darkMode ? 'text-slate-600' : 'text-slate-300'} opacity-40`
                }`}>
                  <span className="flex items-center gap-1">
                    <button
                      onClick={() => useEnergyStore.getState().openHelpToItem('Feed-in Tariff (Export Credits)')}
                      className={`shrink-0 p-0.5 rounded-full transition-colors cursor-pointer ${
                        isSolar
                          ? darkMode ? 'text-emerald-500/40 hover:text-emerald-300' : 'text-emerald-600/40 hover:text-emerald-700'
                          : darkMode ? 'text-slate-700 hover:text-slate-500' : 'text-slate-300 hover:text-slate-450'
                      }`}
                      title="Help with Feed-in Tariff (Export Credits)"
                    >
                      <Info size={10} />
                    </button>
                    <Activity size={10} /> Feed-in Tariff (Export Credits)
                  </span>
                  <span className="font-mono">
                    -${isSolar ? exports.toLocaleString() : '0'}
                  </span>
                </div>

                <div className={`flex items-center justify-between text-xs transition-colors duration-300 ${
                  isSolar && floorAdjustment > 0
                    ? 'text-amber-500 font-bold' 
                    : `${darkMode ? 'text-slate-600' : 'text-slate-300'} opacity-40`
                }`}>
                  <span className="flex items-center gap-1">
                    <button
                      onClick={() => useEnergyStore.getState().openHelpToItem('Daily Minimum Floor Adjustment')}
                      className={`shrink-0 p-0.5 rounded-full transition-colors cursor-pointer ${
                        isSolar && floorAdjustment > 0
                          ? 'text-amber-500 hover:text-amber-405'
                          : darkMode ? 'text-slate-700 hover:text-slate-500' : 'text-slate-300 hover:text-slate-450'
                      }`}
                      title="Help with Daily Minimum Floor Adjustment"
                    >
                      <Info size={10} />
                    </button>
                    Daily Minimum Floor Adjustment
                  </span>
                  <span className="font-mono">
                    +${isSolar ? floorAdjustment.toLocaleString() : '0'}
                  </span>
                </div>

                <div className={`h-px my-2 ${darkMode ? 'bg-white/5' : 'bg-slate-100'}`} />

                <div className="flex items-center justify-between">
                  <span className={`flex items-center gap-1 text-xs font-black uppercase ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    <button
                      onClick={() => useEnergyStore.getState().openHelpToItem('Net Retail Bill')}
                      className={`shrink-0 p-0.5 rounded-full transition-colors cursor-pointer ${
                        darkMode ? 'text-slate-600 hover:text-emerald-500' : 'text-slate-400 hover:text-emerald-600'
                      }`}
                      title="Help with Net Retail Bill"
                    >
                      <Info size={10} />
                    </button>
                    Net Retail Bill
                  </span>
                  <div className="text-right">
                    <span className={`text-sm md:text-base font-black ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                      ${net.toLocaleString()}
                    </span>
                    <div className={`text-[8px] font-black uppercase text-emerald-500 ${net === fixed ? '' : 'invisible select-none'}`}>
                      Fixed Charge Minimum Met
                    </div>
                  </div>
                </div>

                <div className="mt-4 pt-3 border-t border-dashed dark:border-white/5 border-slate-100 flex flex-wrap gap-2 justify-between items-center min-h-[30px]">
                  <div className={`text-[9px] font-black uppercase px-2.5 py-1 rounded-lg flex items-center gap-1 transition-colors duration-300 ${
                    isSolar
                      ? darkMode ? 'bg-emerald-500/10 text-emerald-400' : 'bg-emerald-50 text-emerald-700'
                      : darkMode ? 'bg-white/5 text-slate-600' : 'bg-slate-50 text-slate-350 opacity-40'
                  }`}>
                    <button
                      onClick={() => useEnergyStore.getState().openHelpToItem('Solar Savings')}
                      className={`shrink-0 p-0.5 rounded-full transition-colors cursor-pointer ${
                        isSolar
                          ? darkMode ? 'text-emerald-400/40 hover:text-emerald-300' : 'text-emerald-700/40 hover:text-emerald-800'
                          : darkMode ? 'text-slate-700 hover:text-slate-500' : 'text-slate-300 hover:text-slate-450'
                      }`}
                      title="Help with Solar Savings"
                    >
                      <Info size={10} />
                    </button>
                    Solar Savings: <span className="font-mono ml-0.5">${isSolar ? solarSavings.toLocaleString() : '0'}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Energy Flow Summary Card */}
            <div className={`p-5 rounded-2xl border ${darkMode ? 'bg-slate-900/40 border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}>
              <h4 className={`text-[10px] font-black uppercase tracking-wider mb-4 flex items-center gap-1.5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                <Activity size={12} className="text-blue-500" />
                Energy Flow Summary
              </h4>

              <div className="space-y-3.5">
                <div className="flex items-center justify-between text-xs">
                  <span className={`flex items-center gap-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    <button
                      onClick={() => useEnergyStore.getState().openHelpToItem('Total Simulated Consumption')}
                      className={`shrink-0 p-0.5 rounded-full transition-colors cursor-pointer ${
                        darkMode ? 'text-slate-600 hover:text-emerald-500' : 'text-slate-400 hover:text-emerald-600'
                      }`}
                      title="Help with Total Simulated Consumption"
                    >
                      <Info size={10} />
                    </button>
                    Total Simulated Consumption
                  </span>
                  <span className={`font-mono font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                    {totalLoad.toLocaleString()} kWh
                  </span>
                </div>

                <div className="space-y-1.5">
                  <div className="flex justify-between text-[9px] font-black uppercase opacity-70">
                    <span>Solar Self-Use: {isSolar ? totalNetSelfUseKwh.toLocaleString() : '0'} kWh</span>
                    <span>Grid Imports: {totalImportsKwh.toLocaleString()} kWh</span>
                  </div>
                  <div className="w-full h-2 rounded-full overflow-hidden bg-slate-200 dark:bg-slate-800 flex">
                    <div
                      className="h-full bg-emerald-500 transition-all duration-500"
                      style={{ width: `${isSolar ? Math.max(0, Math.min(100, (totalNetSelfUseKwh / (totalLoad || 1)) * 100)) : 0}%` }}
                    />
                    <div
                      className="h-full bg-blue-500 transition-all duration-500"
                      style={{ width: `${isSolar ? Math.max(0, Math.min(100, (totalImportsKwh / (totalLoad || 1)) * 100)) : 100}%` }}
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between text-xs">
                  <span className={`flex items-center gap-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    <button
                      onClick={() => useEnergyStore.getState().openHelpToItem('Effective Import Rate')}
                      className={`shrink-0 p-0.5 rounded-full transition-colors cursor-pointer ${
                        darkMode ? 'text-slate-600 hover:text-emerald-500' : 'text-slate-400 hover:text-emerald-600'
                      }`}
                      title="Help with Effective Import Rate"
                    >
                      <Info size={10} />
                    </button>
                    Effective Import Rate
                  </span>
                  <span className={`font-mono font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                    {avgGridImportRate > 0 ? `$${avgGridImportRate.toFixed(4)} / kWh` : 'N/A'}
                  </span>
                </div>

                <div className={`pt-2 border-t border-dashed dark:border-white/5 border-slate-100 space-y-2 transition-opacity duration-300 ${
                  isSolar ? '' : 'opacity-40'
                }`}>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                    {/* Solar Generation */}
                    <div className="relative pl-4">
                      <div className={`text-[8px] font-black uppercase flex items-center ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                        <button
                          onClick={() => useEnergyStore.getState().openHelpToItem('Solar Generation')}
                          className={`absolute left-0 top-0.5 p-0.5 rounded-full transition-colors cursor-pointer ${
                            isSolar
                              ? darkMode ? 'text-slate-600 hover:text-emerald-500' : 'text-slate-400 hover:text-emerald-600'
                              : 'cursor-default pointer-events-none'
                          }`}
                          title="Help with Solar Generation"
                        >
                          <Info size={8} />
                        </button>
                        Solar Generation
                      </div>
                      <div className={`text-xs font-bold font-mono mt-0.5 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                        {isSolar ? totalSolarGenKwh.toLocaleString() : '0'} kWh
                      </div>
                    </div>

                    {/* Solar Exports */}
                    <div className="relative pl-4">
                      <div className={`text-[8px] font-black uppercase flex items-center ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                        <button
                          onClick={() => useEnergyStore.getState().openHelpToItem('Solar Exports')}
                          className={`absolute left-0 top-0.5 p-0.5 rounded-full transition-colors cursor-pointer ${
                            isSolar
                              ? darkMode ? 'text-slate-600 hover:text-emerald-500' : 'text-slate-400 hover:text-emerald-600'
                              : 'cursor-default pointer-events-none'
                          }`}
                          title="Help with Solar Exports"
                        >
                          <Info size={8} />
                        </button>
                        Solar Exports
                      </div>
                      <div className={`text-xs font-bold font-mono mt-0.5 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                        {isSolar ? totalExportKwh.toLocaleString() : '0'} kWh
                      </div>
                    </div>

                    {/* Self-Sufficiency */}
                    <div className="relative pl-4">
                      <div className={`text-[8px] font-black uppercase flex items-center ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                        <button
                          onClick={() => useEnergyStore.getState().openHelpToItem('Self-Sufficiency')}
                          className={`absolute left-0 top-0.5 p-0.5 rounded-full transition-colors cursor-pointer ${
                            isSolar
                              ? darkMode ? 'text-slate-600 hover:text-emerald-500' : 'text-slate-400 hover:text-emerald-600'
                              : 'cursor-default pointer-events-none'
                          }`}
                          title="Help with Self-Sufficiency"
                        >
                          <Info size={8} />
                        </button>
                        Self-Sufficiency
                      </div>
                      <div className={`text-xs font-black mt-0.5 ${isSolar ? 'text-emerald-500' : `${darkMode ? 'text-slate-600' : 'text-slate-400'}`}`}>
                        {isSolar ? Math.round((totalNetSelfUseKwh / (totalLoad || 1)) * 100) : 0}%
                      </div>
                    </div>

                    {/* Solar Utilization */}
                    <div className="relative pl-4">
                      <div className={`text-[8px] font-black uppercase flex items-center ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                        <button
                          onClick={() => useEnergyStore.getState().openHelpToItem('Solar Utilization')}
                          className={`absolute left-0 top-0.5 p-0.5 rounded-full transition-colors cursor-pointer ${
                            isSolar
                              ? darkMode ? 'text-slate-600 hover:text-emerald-500' : 'text-slate-400 hover:text-emerald-600'
                              : 'cursor-default pointer-events-none'
                          }`}
                          title="Help with Solar Utilization"
                        >
                          <Info size={8} />
                        </button>
                        Solar Utilization
                      </div>
                      <div className={`text-xs font-black mt-0.5 ${isSolar ? 'text-amber-500' : `${darkMode ? 'text-slate-600' : 'text-slate-400'}`}`}>
                        {isSolar ? `${Math.round((totalGrossSelfUseKwh / (totalSolarGenKwh || 1)) * 100)}%` : '0%'}
                      </div>
                    </div>
                  </div>
                </div>

                {config.useEv && (
                  <div className="pt-2 border-t border-dashed dark:border-white/5 border-slate-100 flex items-center justify-between text-xs">
                    <span className={`flex items-center gap-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      <button
                        onClick={() => useEnergyStore.getState().openHelpToItem('EV Charging Share (Gross)')}
                        className={`shrink-0 p-0.5 rounded-full transition-colors cursor-pointer ${
                          darkMode ? 'text-slate-600 hover:text-emerald-500' : 'text-slate-400 hover:text-emerald-600'
                        }`}
                        title="Help with EV Charging Share (Gross)"
                      >
                        <Info size={10} />
                      </button>
                      EV Charging Share (Gross)
                    </span>
                    <span className={`font-mono font-bold text-teal-500`}>
                      ${evMonthly.toLocaleString()} ({Math.round(evMonthly * 100 / (net || 1)) || 0}%)
                    </span>
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>

      </div>
    </div>
  )
}
